import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type InboxItem, type OrderSummary, type Page } from '../shared/api';
import {
  AdminIcon, AgentIcon, AuditIcon, BeneficiaryIcon, DashIcon, MenuIcon, OrdersIcon, PayrollIcon, ShieldIcon,
  UserIcon, useKeyboardShortcut, useMediaQuery,
} from '../shared/ui';
import { InstallBanner } from '../shared/InstallBanner';
import type { Role } from '../shared/api';
import { useAuth } from './auth';

// `roles: undefined` means everyone. The nav mirrors the server's permission
// catalogue — the API refuses regardless, but showing a link that 403s is a
// poor experience.
const NAV: Array<{
  to: string; label: string; Icon: () => JSX.Element;
  end?: boolean; badge?: boolean; roles?: Role[];
}> = [
  { to: '/', label: 'Dashboard', Icon: DashIcon, end: true },
  { to: '/orders', label: 'Payment orders', Icon: OrdersIcon },
  {
    to: '/approvals', label: 'Approvals', Icon: ShieldIcon, badge: true,
    roles: ['VERIFIER', 'APPROVER_L1', 'APPROVER_L2', 'APPROVER_L3', 'ADMIN'],
  },
  { to: '/findings', label: 'Findings', Icon: AgentIcon },
  { to: '/audit', label: 'Audit trail', Icon: AuditIcon },
  { to: '/beneficiaries', label: 'Beneficiaries', Icon: BeneficiaryIcon, roles: ['ADMIN', 'FINANCE_OPS', 'MAKER'] },
  { to: '/payroll/employees', label: 'Payroll', Icon: PayrollIcon, roles: ['ADMIN', 'FINANCE_OPS'] },
  { to: '/admin', label: 'Access & authority', Icon: AdminIcon, roles: ['ADMIN'] },
];

export function Shell() {
  const { user, signOut } = useAuth();
  const isMobile = useMediaQuery('(max-width: 820px)');
  const [railOpen, setRailOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useKeyboardShortcut('k', () => setPaletteOpen(true));
  useEffect(() => { setRailOpen(false); }, [isMobile]);

  const { data: inbox } = useQuery({
    queryKey: ['inbox'],
    queryFn: () => api.get<{ items: InboxItem[]; total: number }>('/approvals/inbox'),
    refetchInterval: 30_000,
  });

  const initials = (user?.display_name ?? '')
    .split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="shell">
      {isMobile && railOpen && (
        <div className="scrim" onClick={() => setRailOpen(false)} role="presentation" />
      )}

      <aside className={`rail${railOpen ? ' open' : ''}`} aria-label="Main navigation">
        <div className="rail-head">
          <div className="wordmark">POAAS</div>
          <div className="wordmark-sub">Approval Automation</div>
        </div>

        <nav className="nav">
          {NAV.filter((item) => !item.roles || (user && item.roles.includes(user.role)))
            .map(({ to, label, Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              onClick={() => setRailOpen(false)}
            >
              <Icon />
              {label}
              {badge && inbox && inbox.total > 0 && (
                <span className="nav-count">{inbox.total}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="rail-foot">
          <NavLink
            to="/profile"
            className="row gap-10 grow"
            style={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
            onClick={() => setRailOpen(false)}
            title="Your profile"
          >
            <div className="avatar" aria-hidden="true">{initials}</div>
            <div className="grow" style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {user?.display_name}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--ghost)' }}>
                {user?.role.replace(/_/g, ' ')}
              </div>
            </div>
          </NavLink>
          <button className="btn btn-ghost btn-sm" onClick={signOut} title="Sign out">
            Exit
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {isMobile && (
            <button
              className="rail-toggle"
              onClick={() => setRailOpen((v) => !v)}
              aria-label="Toggle navigation"
              aria-expanded={railOpen}
            >
              <MenuIcon />
            </button>
          )}
          <div className="grow" />
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
          >
            Search <kbd className="mono ghost" style={{ fontSize: 11 }}>⌘K</kbd>
          </button>
          <NavLink
            to="/profile"
            className={({ isActive }) => `btn btn-ghost btn-sm${isActive ? ' active' : ''}`}
            aria-label="Your profile"
            title="Your profile"
          >
            <UserIcon />
            <span className="profile-label">{user?.display_name.split(' ')[0]}</span>
          </NavLink>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
      <InstallBanner />
    </div>
  );
}

/* ── Command palette ───────────────────────────────────────────────────────
   One input for jumping to any voucher or screen. Two days of work, and it
   is the first thing that makes the tool feel like it was built for people
   who use it all day. */

function CommandPalette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['palette', query],
    queryFn: () =>
      api.get<Page<OrderSummary>>(
        `/orders?page_size=6${query ? `&q=${encodeURIComponent(query)}` : ''}`,
      ),
    staleTime: 5_000,
  });

  const commands = useMemo(
    () => [
      { id: 'nav-dash', label: 'Go to dashboard', run: () => navigate('/') },
      { id: 'nav-orders', label: 'Go to payment orders', run: () => navigate('/orders') },
      { id: 'nav-approvals', label: 'Go to approvals', run: () => navigate('/approvals') },
      { id: 'nav-findings', label: 'Go to findings', run: () => navigate('/findings') },
      { id: 'nav-audit', label: 'Go to audit trail', run: () => navigate('/audit') },
      { id: 'new-order', label: 'Create a payment order', run: () => navigate('/orders?new=1') },
      { id: 'nav-beneficiaries', label: 'Go to beneficiaries', run: () => navigate('/beneficiaries') },
      { id: 'nav-payroll', label: 'Go to payroll', run: () => navigate('/payroll/employees') },
      { id: 'nav-payroll-runs', label: 'Go to pay runs', run: () => navigate('/payroll/runs') },
      { id: 'nav-profile', label: 'Go to my profile', run: () => navigate('/profile') },
      { id: 'nav-admin', label: 'Go to access & authority', run: () => navigate('/admin') },
    ].filter((c) => c.label.toLowerCase().includes(query.toLowerCase())),
    [query, navigate],
  );

  const orders = data?.items ?? [];
  const rows = [
    ...orders.map((o) => ({
      id: o.id,
      label: `${o.voucher_no} · ${o.total.formatted}`,
      hint: `${o.purpose.replace(/_/g, ' ')} · ${o.state.replace(/_/g, ' ')}`,
      run: () => navigate(`/orders/${o.id}`),
    })),
    ...commands.map((c) => ({ id: c.id, label: c.label, hint: 'Command', run: c.run })),
  ];

  useEffect(() => { setCursor(0); }, [query]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && rows[cursor]) { rows[cursor].run(); onClose(); }
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="overlay" onClick={onClose} role="presentation" style={{ alignItems: 'flex-start', paddingTop: '12vh' }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        style={{ padding: 0, overflow: 'hidden' }}
      >
        <input
          className="input"
          autoFocus
          placeholder="Search vouchers or type a command…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          style={{
            border: 'none', borderRadius: 0, fontSize: 16, padding: '17px 19px',
            borderBottom: '1px solid var(--line)',
          }}
          aria-label="Search"
        />
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {rows.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--faint)' }}>
              Nothing matches “{query}”.
            </div>
          )}
          {rows.map((row, i) => (
            <button
              key={row.id}
              className="row between"
              onClick={() => { row.run(); onClose(); }}
              onMouseEnter={() => setCursor(i)}
              style={{
                width: '100%', padding: '11px 19px', border: 'none', cursor: 'pointer',
                background: i === cursor ? 'var(--raised)' : 'transparent',
                color: 'var(--paper)', textAlign: 'left', fontFamily: 'var(--ui)', fontSize: 13.5,
              }}
            >
              <span className="mono" style={{ fontSize: 12.5 }}>{row.label}</span>
              <span className="ghost" style={{ fontSize: 11 }}>{row.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
